import { useEffect, useRef, useState } from "react";

import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import Papa from "papaparse";

import TilleyDataGrid from "./TilleyDataGrid";

const PrintFriendlyTable = ({ columns, rows, emptyMessage }) => (
  <Table className="only-print printable-tilley-table" size="small">
    <TableHead>
      <TableRow>
        {columns.map((column) => (
          <TableCell key={column.field} style={{ textAlign: column.align }}>
            {column.headerName}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {emptyMessage ? (
        <TableRow>
          <TableCell colSpan={Math.max(columns.length, 1)} style={{ textAlign: "center" }}>
            {emptyMessage}
          </TableCell>
        </TableRow>
      ) : (
        rows.map((row, index) => (
          <TableRow key={index} className="">
            {columns.map((column) => (
              <TableCell style={{ textAlign: column.align }} key={column.field}>
                {row[column.headerName]}
              </TableCell>
            ))}
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
);

export const PrintableTilleyDataGrid = ({
  gridKey,
  hideColumns = [],
  pageSizeOptions = [5, 10, 15, 25, 50, 100],
  defaultPageSize = 25,
  rows = [],
  columns = [],
  emptyMessage = "No rows",
  getRowId,
  columnVisibilityModel = {},
  isRowSelectable = () => true,
  checkboxSelection = false,
  disableRowSelectionOnClick,
  rowSelectionModel,
  onRowSelectionModelChange = () => {},
  getRowHeight = null,
  hideFooterPagination = false,
  hideFooterSelectedRowCount = false,
  sx = {},
  isLoading = false,
  isScrollToTopOnPageChange = false,
  ...props
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const gridRef = useRef();
  const [printColumns, setPrintColumns] = useState([]);
  const [printRows, setPrintRows] = useState([]);

  useEffect(() => {
    if (isPrinting && gridRef.current.current) {
      gridRef.current.current.setRowSelectionModel([]);
      const csvData = gridRef.current.current.getDataAsCsv();
      const parsedData = Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
        error: function (error, file) {
          console.warn("Error while parsing:", error, file);
        },
      });

      const allColumns = gridRef.current.current.getAllColumns();

      const enhancedColumns = allColumns
        .filter((col) => !hideColumns.includes(col.field))
        .map((col) => ({
          field: col.field,
          headerName: col.headerName || col.field,
          type: col.type || "string",
          align: col.align || "left",
        }));

      setPrintColumns(enhancedColumns);
      setPrintRows(parsedData.data);
    }
  }, [isPrinting]);

  useEffect(() => {
    const handleBeforePrint = () => setIsPrinting(true);
    const handleAfterPrint = () => setIsPrinting(false);
    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  return (
    <>
      <PrintFriendlyTable
        columns={
          printColumns.length ? printColumns : columns.filter((col) => !hideColumns.includes(col.field))
        }
        rows={printRows}
        emptyMessage={!rows?.length ? emptyMessage : undefined}
      />
      <TilleyDataGrid
        className="no-print"
        getFilteredRows={gridRef}
        gridKey={gridKey}
        pageSizeOptions={pageSizeOptions}
        defaultPageSize={defaultPageSize}
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        columnVisibilityModel={columnVisibilityModel}
        isRowSelectable={isRowSelectable}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        getRowHeight={getRowHeight}
        hideFooterPagination={hideFooterPagination}
        hideFooterSelectedRowCount={hideFooterSelectedRowCount}
        sx={sx}
        isLoading={isLoading}
        isScrollToTopOnPageChange={isScrollToTopOnPageChange}
        {...props}
      />
    </>
  );
};
